import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CrewList, { type CrewMember } from "@/components/company/CrewList";
import JobSchedule from "@/components/company/JobSchedule";
import OnboardingChecklist from "@/components/company/OnboardingChecklist";
import CustomerList from "@/components/company/CustomerList";
import Today from "@/components/company/Today";
import CompanySettings from "@/components/company/Settings";
import Billing from "@/components/company/Billing";
import Reports from "@/components/company/Reports";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  UsersRound,
  CalendarDays,
  ListChecks,
  Receipt,
  BarChart3,
  LayoutDashboard,
  Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";

type Section =
  | "today"
  | "setup"
  | "customers"
  | "crew"
  | "schedule"
  | "billing"
  | "reports"
  | "settings";
type CustomerTab = "legacy" | "mowzi" | "missed";

const WORK_NAV: { id: Section; title: string; icon: LucideIcon }[] = [
  { id: "today", title: "Today", icon: LayoutDashboard },
  { id: "schedule", title: "Job Schedule", icon: CalendarDays },
  { id: "crew", title: "Crew", icon: UsersRound },
];

const FINANCES_NAV: { id: Section; title: string; icon: LucideIcon }[] = [
  { id: "billing", title: "Billing", icon: Receipt },
];

const INSIGHTS_NAV: { id: Section; title: string; icon: LucideIcon }[] = [
  { id: "reports", title: "Reports", icon: BarChart3 },
];

const COMPANY_NAV: { id: Section; title: string; icon: LucideIcon }[] = [
  { id: "setup", title: "Getting started", icon: ListChecks },
  { id: "settings", title: "Settings", icon: SettingsIcon },
];

const CUSTOMER_TABS: { id: CustomerTab; title: string }[] = [
  { id: "legacy", title: "My customers" },
  { id: "mowzi", title: "Mowzi customers" },
  { id: "missed", title: "Missed service" },
];



export default function CompanyDashboard() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [company, setCompany] = useState<{ name: string; code: string } | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [ready, setReady] = useState(false);
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [jobCount, setJobCount] = useState(0);
  const [customerRefresh, setCustomerRefresh] = useState(0);
  const [crewRefresh, setCrewRefresh] = useState(0);
  const [setupComplete, setSetupComplete] = useState(false);
  const [section, setSection] = useState<Section>("today");
  const [customerTab, setCustomerTab] = useState<CustomerTab>("legacy");

  const handleCustomersChanged = () => {
    setCustomerRefresh((v) => v + 1);
    if (companyId) {
      supabase.functions
        .invoke("geocode-customers", { body: { company_id: companyId } })
        .catch((e) => console.error("geocode-customers failed:", e));
    }
  };



  useEffect(() => {
    if (!loading && !user) navigate("/auth?mode=login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: profile }, { data: roles }] = await Promise.all([
        supabase.from("profiles").select("company_id").eq("id", user.id).maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", user.id),
      ]);
      const owner = (roles ?? []).some((r) => r.role === "owner");
      // Crew members belong in the field app, not the office dashboard.
      if (!owner && (roles ?? []).some((r) => r.role === "mower")) {
        navigate("/crew", { replace: true });
        return;
      }
      setIsOwner(owner);
      const cid = profile?.company_id ?? null;
      setCompanyId(cid);
      if (cid) {
        const [{ data }, { count }] = await Promise.all([
          supabase.from("companies").select("name, code").eq("id", cid).maybeSingle(),
          supabase.from("jobs").select("id", { count: "exact", head: true }).eq("company_id", cid),
        ]);
        setCompany(data);
        setJobCount(count ?? 0);
      }
      setReady(true);
    })();
  }, [user]);

  if (loading || !user) return null;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <Sidebar collapsible="icon">
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Work</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {WORK_NAV.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        isActive={section === item.id}
                        onClick={() => setSection(item.id)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Customers</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {CUSTOMER_TABS.map((t) => (
                    <SidebarMenuItem key={t.id}>
                      <SidebarMenuButton
                        isActive={section === "customers" && customerTab === t.id}
                        onClick={() => {
                          setSection("customers");
                          setCustomerTab(t.id);
                        }}
                      >
                        <span>{t.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Finances</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {FINANCES_NAV.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        isActive={section === item.id}
                        onClick={() => setSection(item.id)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Insights</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {INSIGHTS_NAV.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        isActive={section === item.id}
                        onClick={() => setSection(item.id)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Company</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {COMPANY_NAV.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        isActive={section === item.id}
                        onClick={() => setSection(item.id)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="border-b">
            <div className="flex items-center justify-between px-3 py-3 sm:px-6 sm:py-4">
              <div className="flex items-center gap-2">
                <SidebarTrigger />
                <Link to="/" className="font-display text-xl font-bold text-primary">
                  Mowzi
                </Link>
              </div>
              <Button variant="ghost" size="sm" onClick={() => signOut().then(() => navigate("/"))}>
                Sign out
              </Button>
            </div>
          </header>

          <main className="w-full space-y-4 px-3 py-4 sm:space-y-6 sm:px-6 sm:py-8">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h1 className="font-display text-2xl font-bold">
                  {company?.name ?? "Company dashboard"}
                </h1>
                {company?.code && (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>
                      Company code: <span className="font-mono font-semibold">{company.code}</span>
                    </span>
                    {setupComplete && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        Active
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>

            {ready && !companyId && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">No company linked</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  Your account isn't linked to a company yet. Sign up as a business owner or ask your
                  owner for the 4-letter company code.
                </CardContent>
              </Card>
            )}

            {ready && companyId && (
              <>
                {section === "today" && (
                  <Today
                    companyId={companyId}
                    companyName={company?.name}
                    onGoTo={(s) => {
                      setSection(s);
                      if (s === "customers") setCustomerTab("missed");
                    }}
                  />
                )}

                <div className={section === "setup" ? "" : "hidden"}>

                  <OnboardingChecklist
                    companyId={companyId}
                    isOwner={isOwner}
                    crewCount={crew.length}
                    jobCount={jobCount}
                    companyName={company?.name}
                    onCustomersChanged={handleCustomersChanged}
                    onCompleteChange={setSetupComplete}
                    onGoTo={(s) => setSection(s)}
                  />
                </div>

                <div className={section === "customers" ? "" : "hidden"}>
                  <CustomerList
                    companyId={companyId}
                    isOwner={isOwner}
                    refreshKey={customerRefresh}
                    onCustomersChanged={handleCustomersChanged}
                    source={customerTab}
                  />

                </div>

                <div className={section === "crew" ? "" : "hidden"}>
                  <CrewList
                    companyId={companyId}
                    isOwner={isOwner}
                    companyCode={company?.code}
                    companyName={company?.name}
                    currentUserId={user.id}
                    refreshKey={crewRefresh}
                    onLoaded={setCrew}
                  />
                </div>

                <div className={section === "schedule" ? "" : "hidden"}>
                  <JobSchedule
                    companyId={companyId}
                    isOwner={isOwner}
                    crew={crew}
                    refreshKey={customerRefresh}
                    onRefreshCrew={() => setCrewRefresh((v) => v + 1)}
                  />
                </div>

                {section === "billing" && <Billing companyId={companyId} isOwner={isOwner} />}

                {section === "reports" && <Reports companyId={companyId} />}

                {section === "settings" && (
                  <CompanySettings
                    companyId={companyId}
                    isOwner={isOwner}
                    onChanged={() => setCustomerRefresh((v) => v + 1)}
                  />
                )}

              </>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
